/**
 * ThisMonth Tab Mockup - UX/UI Audit Enhancement Preview
 *
 * This is a MOCKUP showing proposed improvements to the ThisMonth tab.
 * It uses sample data and is completely self-contained.
 *
 * PROPOSED CHANGES (marked with "ENHANCED"):
 * 1. Sticky Section Navigation - Jump between sections quickly
 * 2. Collapsible Sections - Reduce scroll fatigue
 * 3. Key Insight Surfaced - Diagnosis summary moved to top
 * 4. Consolidated Metric Cards - Related metrics grouped together
 * 5. Visual Section Separators - Clearer hierarchy
 * 6. Consolidated Efficiency Chart - 3 separate BarCharts merged into 1
 *
 * Created: December 3, 2025 for UX/UI Audit
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LabelList
} from 'recharts';

// ============================================
// SAMPLE DATA - Mimics real data patterns
// ============================================
const CURRENT_MONTH = 'December';
const BUSINESS_DAYS_ELAPSED = 3;
const BUSINESS_DAYS_IN_MONTH = 22;
const BUSINESS_DAYS_REMAINING = 19;

const SAMPLE_METRICS = {
  appsIn: 12,
  approved: 8,
  funded: 4,
  revenue: 145000,
  fundedAmount: 380000,
  calls: 342,
  sms: 189,
  totalTalkTime: 285,
  avgTalkTimePerCall: 0.83,
  callsToApps: 3.5,
  smsToApps: 6.3,
  appsToApproved: 66.7,
  approvedToFunded: 50.0,
  revenuePerCall: 424,
  revenuePerSMS: 767,
  avgDealSize: 36250,
  // Submission metrics
  submissionsWon: 12,
  submissionsLost: 8,
  submissionsInProgress: 15,
  totalSubmissions: 35,
  totalDeclines: 6,
  totalDeals: 22,
  avgSubmissionsPerDeal: 1.6,
  successRate: 18.2
};

const SAMPLE_GOALS = {
  apps: 45,
  approved: 30,
  funded: 15,
  revenue: 500000,
  fundedAmount: 1200000
};

const SAMPLE_WEEKLY_DATA = [
  { week: 'W1', apps: 5, approved: 3, funded: 2, revenue: 72000, calls: 145, sms: 82, activityPoints: 195 },
  { week: 'W2', apps: 4, approved: 3, funded: 1, revenue: 38000, calls: 112, sms: 58, activityPoints: 152 },
  { week: 'W3', apps: 3, approved: 2, funded: 1, revenue: 35000, calls: 85, sms: 49, activityPoints: 115 }
];

const SAMPLE_REP_DATA = [
  { name: 'John', apps: 4, approved: 3, revenue: 52000, callToApp: 4.2, pace: 112 },
  { name: 'Sarah', apps: 3, approved: 2, revenue: 38000, callToApp: 3.8, pace: 95 },
  { name: 'Mike', apps: 3, approved: 2, revenue: 32000, callToApp: 3.1, pace: 78 },
  { name: 'Jane', apps: 2, approved: 1, revenue: 23000, callToApp: 2.9, pace: 62 }
];

// Efficiency metrics by rep (for consolidated chart)
// Original had 3 separate charts - now consolidated into 1
const SAMPLE_EFFICIENCY_DATA = [
  { name: 'John', avgDealSize: 13000, revenuePerApp: 13000, appsPerTenK: 0.77 },
  { name: 'Sarah', avgDealSize: 12667, revenuePerApp: 12667, appsPerTenK: 0.79 },
  { name: 'Mike', avgDealSize: 10667, revenuePerApp: 10667, appsPerTenK: 0.94 },
  { name: 'Jane', avgDealSize: 11500, revenuePerApp: 11500, appsPerTenK: 0.87 }
];

const SAMPLE_PACK_SOURCE = [
  { name: 'Web Lead', appsIn: 5, approved: 4, funded: 2, revenue: 72000, avgDealSize: 36000, convRate: 80 },
  { name: 'Referral', appsIn: 3, approved: 2, funded: 1, revenue: 42000, avgDealSize: 42000, convRate: 67 },
  { name: 'Cold Call', appsIn: 2, approved: 1, funded: 1, revenue: 31000, avgDealSize: 31000, convRate: 50 },
  { name: 'Missing', appsIn: 2, approved: 1, funded: 0, revenue: 0, avgDealSize: 0, convRate: 50 }
];

const SAMPLE_DIAGNOSIS = {
  topPerformer: { name: 'John', callToApp: 4.2 },
  needsHelp: { name: 'Jane', callToApp: 2.9 },
  biggestGap: 'Apps',
  pacePercentage: 82,
  diagnosis: 'Activity is solid (+12% calls vs last month), but conversion is down 2pp. Focus on quality over quantity - Jane needs coaching on call scripts.'
};

// ============================================
// MAIN MOCKUP COMPONENT
// ============================================
export default function ThisMonthTabMockup() {
  const [activeSection, setActiveSection] = useState('snapshot');
  const [collapsedSections, setCollapsedSections] = useState({});

  // Set unique browser tab title
  useEffect(() => {
    document.title = '🎨 MOCKUP: ThisMonth Tab';
  }, []);

  // Refs for scrolling
  const sectionRefs = {
    snapshot: useRef(null),
    goals: useRef(null),
    weekly: useRef(null),
    why: useRef(null)
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(value);

  const formatPercent = (value) => `${value.toFixed(1)}%`;

  // Calculate pacing
  const expectedByNow = (goal) => (goal / BUSINESS_DAYS_IN_MONTH) * BUSINESS_DAYS_ELAPSED;
  const pacePercentage = (current, goal) => {
    const expected = expectedByNow(goal);
    return expected > 0 ? (current / expected) * 100 : 0;
  };

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      {/* Header */}
      <div className="dashboard-header">
        <h1>Sales Performance Dashboard</h1>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          backgroundColor: '#fef3c7',
          padding: '8px 16px',
          borderRadius: '6px',
          border: '2px solid #f59e0b'
        }}>
          <span style={{ fontSize: '18px' }}>🎨</span>
          <span style={{ fontWeight: 600, color: '#92400e' }}>
            MOCKUP MODE - Proposed ThisMonth Tab Enhancements
          </span>
        </div>
      </div>

      {/* Tab Navigation (just for visual context) */}
      <nav className="tab-navigation">
        <button>Overview</button>
        <button className="active">This Month</button>
        <button>Last Month</button>
        <button>Historical</button>
      </nav>

      {/* ============================================ */}
      {/* ENHANCED: Sticky Section Navigation         */}
      {/* ============================================ */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        padding: '12px 0',
        borderBottom: '2px solid #e2e8f0',
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginRight: '8px' }}>
            JUMP TO:
          </span>
          {[
            { id: 'snapshot', label: '📊 Performance', icon: '1' },
            { id: 'goals', label: '🎯 Goals', icon: '2' },
            { id: 'weekly', label: '📅 Weekly', icon: '3' },
            { id: 'why', label: '🔍 WHY Analysis', icon: '4' }
          ].map(section => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: activeSection === section.id ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                backgroundColor: activeSection === section.id ? '#eff6ff' : 'white',
                color: activeSection === section.id ? '#1e40af' : '#475569',
                fontWeight: activeSection === section.id ? 600 : 500,
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              {section.label}
            </button>
          ))}

          {/* Enhancement indicator */}
          <div style={{
            marginLeft: 'auto',
            padding: '6px 12px',
            backgroundColor: '#d1fae5',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#065f46',
            fontWeight: 600
          }}>
            ✨ NEW: Sticky section navigation
          </div>
        </div>
      </div>

      <main className="dashboard-content">
        <div className="this-month-tab">
          <h2>This Month: {CURRENT_MONTH} (Real-Time)</h2>
          <p className="tab-subtitle">Team Performance</p>

          {/* ============================================ */}
          {/* ENHANCED: Key Insight Surfaced to Top       */}
          {/* ============================================ */}
          <div style={{
            padding: '16px 20px',
            marginBottom: '24px',
            borderRadius: '12px',
            border: '3px solid #3b82f6',
            backgroundColor: '#eff6ff',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '16px'
          }}>
            <div style={{ fontSize: '32px' }}>💡</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e40af', marginBottom: '8px' }}>
                KEY INSIGHT (from WHY Analysis)
              </div>
              <div style={{ fontSize: '16px', color: '#334155', lineHeight: 1.6 }}>
                {SAMPLE_DIAGNOSIS.diagnosis}
              </div>
              <div style={{ marginTop: '12px', display: 'flex', gap: '16px', fontSize: '14px' }}>
                <span style={{ color: '#059669' }}>
                  ⬆ Top: {SAMPLE_DIAGNOSIS.topPerformer.name} ({SAMPLE_DIAGNOSIS.topPerformer.callToApp}% call→app)
                </span>
                <span style={{ color: '#dc2626' }}>
                  ⬇ Needs Help: {SAMPLE_DIAGNOSIS.needsHelp.name} ({SAMPLE_DIAGNOSIS.needsHelp.callToApp}% call→app)
                </span>
              </div>
            </div>
            <div style={{
              padding: '6px 12px',
              backgroundColor: '#d1fae5',
              borderRadius: '4px',
              fontSize: '11px',
              color: '#065f46',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}>
              ✨ ENHANCED: Moved from bottom
            </div>
          </div>

          {/* Executive Summary */}
          <section style={{
            padding: '20px',
            marginBottom: '24px',
            borderRadius: '12px',
            border: `3px solid ${SAMPLE_DIAGNOSIS.pacePercentage >= 90 ? '#10b981' : SAMPLE_DIAGNOSIS.pacePercentage >= 70 ? '#f59e0b' : '#ef4444'}`,
            backgroundColor: SAMPLE_DIAGNOSIS.pacePercentage >= 90 ? '#f0fdf4' : SAMPLE_DIAGNOSIS.pacePercentage >= 70 ? '#fffbeb' : '#fef2f2'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1e293b' }}>
                {CURRENT_MONTH.toUpperCase()} STATUS: 🟡 ATTENTION
              </h2>
              <div style={{ fontSize: '13px', color: '#64748b' }}>
                Day {BUSINESS_DAYS_ELAPSED} of {BUSINESS_DAYS_IN_MONTH} business days
              </div>
            </div>
            <div style={{ display: 'grid', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                <span style={{ fontWeight: 600, minWidth: '80px' }}>Revenue:</span>
                <span>{formatCurrency(SAMPLE_METRICS.revenue)} of {formatCurrency(SAMPLE_GOALS.revenue)} goal</span>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 600,
                  backgroundColor: '#fef3c7',
                  color: '#92400e'
                }}>
                  {SAMPLE_DIAGNOSIS.pacePercentage}% of pace
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                <span style={{ fontWeight: 600, minWidth: '80px' }}>Biggest gap:</span>
                <span>{SAMPLE_DIAGNOSIS.biggestGap} — need +2/day to catch up</span>
              </div>
            </div>
          </section>

          {/* ============================================ */}
          {/* SECTION 1: PERFORMANCE SNAPSHOT             */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.snapshot}
            className="performance-snapshot-section"
            style={{
              scrollMarginTop: '80px',
              marginTop: '24px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0'
            }}
          >
            {/* ENHANCED: Collapsible Section Header */}
            <div
              onClick={() => toggleSection('snapshot')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>📊 Performance Snapshot</h2>
                <p className="section-subtitle" style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  How are we performing this month?
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  padding: '4px 8px',
                  backgroundColor: '#d1fae5',
                  borderRadius: '4px',
                  fontSize: '10px',
                  color: '#065f46',
                  fontWeight: 600
                }}>
                  ✨ Collapsible
                </span>
                <span style={{
                  fontSize: '20px',
                  color: '#64748b',
                  transform: collapsedSections.snapshot ? 'rotate(-90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}>
                  ▼
                </span>
              </div>
            </div>

            {/* Section Content (collapsible) */}
            {!collapsedSections.snapshot && (
              <>
                {/* Performance Summary Cards */}
                <div className="performance-summary" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <MetricCard
                    label="Apps In"
                    value={SAMPLE_METRICS.appsIn}
                    goal={SAMPLE_GOALS.apps}
                    expected={Math.round(expectedByNow(SAMPLE_GOALS.apps))}
                    businessDay={BUSINESS_DAYS_ELAPSED}
                    formatValue={(v) => v}
                  />
                  <MetricCard
                    label="Approved"
                    value={SAMPLE_METRICS.approved}
                    goal={SAMPLE_GOALS.approved}
                    expected={Math.round(expectedByNow(SAMPLE_GOALS.approved))}
                    businessDay={BUSINESS_DAYS_ELAPSED}
                    formatValue={(v) => v}
                  />
                  <MetricCard
                    label="Funded"
                    value={SAMPLE_METRICS.funded}
                    goal={SAMPLE_GOALS.funded}
                    expected={Math.round(expectedByNow(SAMPLE_GOALS.funded))}
                    businessDay={BUSINESS_DAYS_ELAPSED}
                    formatValue={(v) => v}
                  />
                  <MetricCard
                    label="Revenue"
                    value={SAMPLE_METRICS.revenue}
                    goal={SAMPLE_GOALS.revenue}
                    expected={Math.round(expectedByNow(SAMPLE_GOALS.revenue))}
                    businessDay={BUSINESS_DAYS_ELAPSED}
                    formatValue={formatCurrency}
                  />
                  <MetricCard
                    label="Total Talk Time"
                    value={`${Math.floor(SAMPLE_METRICS.totalTalkTime / 60)}h ${Math.round(SAMPLE_METRICS.totalTalkTime % 60)}m`}
                    subtext={`${SAMPLE_METRICS.totalTalkTime} minutes total`}
                  />
                  <MetricCard
                    label="Avg per Call"
                    value={`${SAMPLE_METRICS.avgTalkTimePerCall.toFixed(1)}m`}
                    subtext={`From ${SAMPLE_METRICS.calls.toLocaleString()} calls`}
                  />
                </div>

                {/* Lender Submissions - Visual separation from above */}
                <div style={{
                  marginTop: '24px',
                  marginBottom: '24px',
                  padding: '20px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                    <h3 className="section-title" style={{ fontSize: '16px', margin: 0 }}>📤 Lender Submissions</h3>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '4px',
                      fontSize: '10px',
                      color: '#92400e',
                      fontWeight: 600,
                      border: '1px solid #f59e0b'
                    }}>
                      🔍 DATA REVIEW NEEDED
                    </span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
                    Deduped by deal (Underwriting + Approved + Funded + Lost)
                  </p>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: '12px'
                  }}>
                    <SmallMetricCard label="Submissions (Won)" value={SAMPLE_METRICS.submissionsWon} color="#059669" />
                    <SmallMetricCard label="Submissions (Lost)" value={SAMPLE_METRICS.submissionsLost} color="#dc2626" />
                    <SmallMetricCard label="In Progress" value={SAMPLE_METRICS.submissionsInProgress} color="#3b82f6" />
                    <SmallMetricCard label="Avg per Deal" value={SAMPLE_METRICS.avgSubmissionsPerDeal.toFixed(1)} />
                    <SmallMetricCard label="Total Declines" value={SAMPLE_METRICS.totalDeclines} color="#f59e0b" />
                    <SmallMetricCard label="Success Rate" value={`${SAMPLE_METRICS.successRate.toFixed(0)}%`} color="#dc2626" />
                  </div>
                </div>

                {/* Revenue Pacing Chart */}
                <div style={{
                  padding: '20px',
                  background: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>📈 Revenue Pacing</h3>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: '#dbeafe',
                      borderRadius: '4px',
                      fontSize: '10px',
                      color: '#1e40af',
                      fontWeight: 600
                    }}>
                      🏷️ 12/5 LABELS
                    </span>
                  </div>
                  <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                    Cumulative revenue vs expected daily trajectory to goal
                  </p>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={[
                      { day: 'Day 1', expected: 22727, actual: 18000 },
                      { day: 'Day 2', expected: 45454, actual: 52000 },
                      { day: 'Day 3', expected: 68181, actual: 145000 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#64748b" />
                      <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} stroke="#64748b" />
                      <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, '']} />
                      <Legend />
                      <Line type="monotone" dataKey="expected" stroke="#94a3b8" strokeDasharray="5 5" name="Target Pace" dot={false}>
                        <LabelList dataKey="expected" position="bottom" style={{ fontSize: 10, fill: '#94a3b8' }} formatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      </Line>
                      <Line type="monotone" dataKey="actual" stroke="#3b82f6" strokeWidth={2} name="Actual" dot={{ r: 3 }}>
                        <LabelList dataKey="actual" position="top" style={{ fontSize: 11, fill: '#3b82f6', fontWeight: 600 }} formatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* MERGED: Channel Performance & Conversion Analysis */}
                <div style={{
                  padding: '20px',
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  marginBottom: '24px',
                  border: '2px solid #e2e8f0'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600 }}>📊 Channel Performance & Conversion Analysis</h3>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>Channel efficiency and conversion rates in one view</p>
                    </div>
                    <span style={{
                      padding: '6px 12px',
                      backgroundColor: '#d1fae5',
                      borderRadius: '4px',
                      fontSize: '11px',
                      color: '#065f46',
                      fontWeight: 600
                    }}>
                      ✨ MERGED: Was 2 separate sections
                    </span>
                  </div>

                  {/* Channel Efficiency Row */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e40af', marginBottom: '12px', borderBottom: '2px solid #eff6ff', paddingBottom: '8px' }}>
                      Channel Efficiency
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                      gap: '12px'
                    }}>
                      <ChannelCard label="Revenue per Call" value={formatCurrency(SAMPLE_METRICS.revenuePerCall)} calculation={`${formatCurrency(SAMPLE_METRICS.revenue)} ÷ ${SAMPLE_METRICS.calls} calls`} />
                      <ChannelCard label="Revenue per SMS" value={formatCurrency(SAMPLE_METRICS.revenuePerSMS)} calculation={`${formatCurrency(SAMPLE_METRICS.revenue)} ÷ ${SAMPLE_METRICS.sms} SMS`} />
                      <ChannelCard label="Call Efficiency" value={formatPercent(SAMPLE_METRICS.callsToApps)} calculation={`${SAMPLE_METRICS.appsIn} apps ÷ ${SAMPLE_METRICS.calls} calls`} />
                      <ChannelCard label="SMS Efficiency" value={formatPercent(SAMPLE_METRICS.smsToApps)} calculation={`${SAMPLE_METRICS.appsIn} apps ÷ ${SAMPLE_METRICS.sms} SMS`} />
                    </div>
                  </div>

                  {/* Conversion Rates Row */}
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e40af', marginBottom: '12px', borderBottom: '2px solid #eff6ff', paddingBottom: '8px' }}>
                      Conversion Rates (vs 6-Month Avg)
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                      gap: '12px'
                    }}>
                      <ConversionCard label="Call → App" rate={SAMPLE_METRICS.callsToApps} historicalAvg={3.2} />
                      <ConversionCard label="SMS → App" rate={SAMPLE_METRICS.smsToApps} historicalAvg={5.8} />
                      <ConversionCard label="App → Approved" rate={SAMPLE_METRICS.appsToApproved} historicalAvg={62.0} />
                      <ConversionCard label="Approved → Funded" rate={SAMPLE_METRICS.approvedToFunded} historicalAvg={55.0} />
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 2: GOAL TRENDING & PROJECTIONS      */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.goals}
            className="goal-trending-section"
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0'
            }}
          >
            {/* Collapsible Header */}
            <div
              onClick={() => toggleSection('goals')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>🎯 Goal Trending & Projections</h2>
                <p className="section-subtitle" style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  How are we trending toward our goals?
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.goals ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.goals && (
              <>
                {/* Gap Closure Math */}
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 style={{ margin: 0, fontSize: '17px', fontWeight: 600 }}>📊 Gap Closure Math</h4>
                    <span style={{ fontSize: '14px', color: '#64748b' }}>{BUSINESS_DAYS_REMAINING} days left</span>
                  </div>
                  <table style={{ width: '100%', fontSize: '15px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Metric</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Current</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Goal</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Gap</th>
                        <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#64748b' }}>Daily Rate Needed</th>
                      </tr>
                    </thead>
                    <tbody>
                      <GapRow metric="Apps" current={12} goal={45} remaining={BUSINESS_DAYS_REMAINING} />
                      <GapRow metric="Approved" current={8} goal={30} remaining={BUSINESS_DAYS_REMAINING} />
                      <GapRow metric="Funded" current={4} goal={15} remaining={BUSINESS_DAYS_REMAINING} />
                      <GapRow metric="Revenue" current={145000} goal={500000} remaining={BUSINESS_DAYS_REMAINING} isCurrency />
                    </tbody>
                  </table>
                </div>

                {/* Rep Performance Table */}
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ marginBottom: '12px', fontSize: '17px', fontWeight: 600 }}>👥 Rep Performance</h4>
                  <table style={{ width: '100%', fontSize: '15px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ textAlign: 'left', padding: '10px 12px' }}>Rep</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Apps</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Approved</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Revenue</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Call→App</th>
                        <th style={{ textAlign: 'right', padding: '10px 12px' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SAMPLE_REP_DATA.map((rep, idx) => (
                        <tr key={rep.name} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                          <td style={{ padding: '10px 12px', fontWeight: 500 }}>{rep.name}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.apps}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.approved}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatCurrency(rep.revenue)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rep.callToApp}%</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor: rep.pace >= 90 ? '#d1fae5' : rep.pace >= 70 ? '#fef3c7' : '#fee2e2',
                              color: rep.pace >= 90 ? '#065f46' : rep.pace >= 70 ? '#92400e' : '#991b1b'
                            }}>
                              {rep.pace}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 3: WEEKLY BREAKDOWN                 */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.weekly}
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0'
            }}
          >
            <div
              onClick={() => toggleSection('weekly')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>📅 Weekly Breakdown</h2>
                <p className="section-subtitle" style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Week-by-week performance with drill-down
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.weekly ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.weekly && (
              <div style={{
                padding: '20px',
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={SAMPLE_WEEKLY_DATA}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="week" stroke="#4a5568" />
                    <YAxis yAxisId="left" stroke="#4a5568" />
                    <YAxis yAxisId="right" orientation="right" stroke="#10b981" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value, name) => {
                      if (name === 'Revenue') return [`$${value.toLocaleString()}`, name];
                      return [value, name];
                    }} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="calls" fill="#3b82f6" name="Calls" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 12, fill: '#3b82f6', fontWeight: 600 }} />
                    <Bar yAxisId="left" dataKey="sms" fill="#9333ea" name="SMS" radius={[4, 4, 0, 0]} label={{ position: 'top', fontSize: 12, fill: '#9333ea', fontWeight: 600 }} />
                    <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={3} name="Revenue" label={{ position: 'top', fontSize: 11, fill: '#10b981', fontWeight: 600, formatter: (v) => `$${(v / 1000).toFixed(0)}k` }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>

          {/* ============================================ */}
          {/* SECTION 4: THE "WHY" ANALYSIS               */}
          {/* ============================================ */}
          {/* Option C: Card-Style Wrapper */}
          <section
            ref={sectionRefs.why}
            style={{
              marginTop: '24px',
              scrollMarginTop: '80px',
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0'
            }}
          >
            {/* Visual separator inside card - Deep Dive badge */}
            <div style={{
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              <span style={{
                display: 'inline-block',
                padding: '6px 16px',
                backgroundColor: '#f1f5f9',
                borderRadius: '20px',
                fontSize: '12px',
                color: '#64748b',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                border: '1px solid #e2e8f0'
              }}>
                Deep Dive Section
              </span>
            </div>
            <div
              onClick={() => toggleSection('why')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                padding: '12px 0',
                borderBottom: '2px solid #e2e8f0',
                marginBottom: '16px'
              }}
            >
              <div>
                <h2 className="section-title" style={{ margin: 0 }}>🔍 The "WHY" Analysis</h2>
                <p className="section-subtitle" style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Why are we performing this way?
                </p>
              </div>
              <span style={{
                fontSize: '20px',
                color: '#64748b',
                transform: collapsedSections.why ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}>
                ▼
              </span>
            </div>

            {!collapsedSections.why && (
              <>
                {/* The Funnel */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ borderTop: '3px double #3b82f6', paddingTop: '16px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e40af', letterSpacing: '0.5px' }}>THE FUNNEL</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>What happened to our leads?</div>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    padding: '20px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    flexWrap: 'wrap',
                    gap: '12px'
                  }}>
                    <FunnelStage label="Calls" value={342} color="#3b82f6" />
                    <FunnelArrow rate="3.5%" />
                    <FunnelStage label="Apps In" value={12} color="#10b981" />
                    <FunnelArrow rate="66.7%" />
                    <FunnelStage label="Approved" value={8} color="#8b5cf6" />
                    <FunnelArrow rate="50%" />
                    <FunnelStage label="Funded" value={4} color="#f59e0b" />
                    <FunnelArrow rate="→" />
                    <FunnelStage label="Revenue" value="$145K" color="#10b981" />
                  </div>
                </div>

                {/* ENHANCED: Consolidated Sales Efficiency Chart */}
                {/* Original had 3 SEPARATE charts - now combined into 1 */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ borderTop: '3px double #10b981', paddingTop: '16px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: '700', color: '#065f46', letterSpacing: '0.5px' }}>SALES EFFICIENCY BY REP</div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Deal size and revenue efficiency metrics</div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: '#d1fae5',
                          borderRadius: '4px',
                          fontSize: '10px',
                          color: '#065f46',
                          fontWeight: 600
                        }}>
                          ✨ CONSOLIDATED: Was 3 separate charts
                        </span>
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: '#dbeafe',
                          borderRadius: '4px',
                          fontSize: '10px',
                          color: '#1e40af',
                          fontWeight: 600
                        }}>
                          🏷️ 12/5 LABELS
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{
                    padding: '20px',
                    background: '#fff',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={SAMPLE_EFFICIENCY_DATA} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} stroke="#64748b" fontSize={11} />
                        <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={12} width={60} />
                        <Tooltip formatter={(value, name) => {
                          if (name === 'appsPerTenK') return [value.toFixed(2), 'Apps per $10k'];
                          return [`$${value.toLocaleString()}`, name === 'avgDealSize' ? 'Avg Deal Size' : 'Revenue per App'];
                        }} />
                        <Legend />
                        <Bar dataKey="avgDealSize" fill="#3b82f6" name="Avg Deal Size" radius={[0, 4, 4, 0]}>
                          <LabelList dataKey="avgDealSize" position="right" style={{ fontSize: 10, fill: '#3b82f6', fontWeight: 600 }} formatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                        </Bar>
                        <Bar dataKey="revenuePerApp" fill="#10b981" name="Revenue per App" radius={[0, 4, 4, 0]}>
                          <LabelList dataKey="revenuePerApp" position="right" style={{ fontSize: 10, fill: '#10b981', fontWeight: 600 }} formatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div style={{ marginTop: '12px', padding: '8px 12px', backgroundColor: '#f0fdf4', borderRadius: '6px', fontSize: '12px', color: '#166534' }}>
                      💡 <strong>John</strong> leads in both average deal size ($13k) and revenue per app.
                    </div>
                  </div>
                </div>

                {/* Performance by Source */}
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '12px' }}>
                    Performance by Source
                    <span style={{ marginLeft: '12px', fontSize: '12px', fontWeight: 'normal', color: '#10b981' }}>
                      (83% data coverage)
                    </span>
                  </h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '10px 12px', textAlign: 'left' }}>Source</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>Apps</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>Approved</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>Revenue</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>Conv%</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>Avg Deal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {SAMPLE_PACK_SOURCE.map((source, idx) => (
                        <tr key={source.name} style={{
                          borderBottom: '1px solid #e2e8f0',
                          background: source.name === 'Missing' ? '#fef3c7' : (idx % 2 === 0 ? 'white' : '#f9fafb')
                        }}>
                          <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                            {source.name === 'Missing' ? '⚠️ ' : ''}{source.name}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{source.appsIn}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{source.approved}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatCurrency(source.revenue)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{source.convRate}%</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{formatCurrency(source.avgDealSize)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ marginTop: '12px', padding: '10px 14px', background: '#f0fdf4', borderRadius: '6px', fontSize: '13px', color: '#166534' }}>
                    💡 <strong>Web Lead</strong> converts best (80%) with {formatCurrency(36000)} avg deal size.
                  </div>
                </div>
              </>
            )}
          </section>

        </div>
      </main>

      {/* Enhancement Legend */}
      <div style={{
        marginTop: '32px',
        padding: '20px',
        backgroundColor: '#eff6ff',
        borderRadius: '8px',
        border: '2px solid #3b82f6'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#1e40af' }}>
          Enhancements in This Mockup:
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          <EnhancementItem
            title="1. Sticky Section Navigation"
            description="Jump between sections quickly without scrolling. Stays fixed at top."
          />
          <EnhancementItem
            title="2. Collapsible Sections"
            description="Click section headers to collapse/expand. Reduces scroll fatigue."
          />
          <EnhancementItem
            title="3. Key Insight Surfaced"
            description="Diagnosis summary moved from bottom to top for immediate visibility."
          />
          <EnhancementItem
            title="4. Visual Section Separators"
            description="Clearer hierarchy with consistent section headers and borders."
          />
          <EnhancementItem
            title="5. Consolidated Efficiency Chart"
            description="3 separate BarCharts (Avg Deal, Rev/App, Apps/$10k) merged into single multi-bar chart."
          />
        </div>
      </div>
    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function MetricCard({ label, value, goal, expected, businessDay, formatValue, subtext }) {
  const displayValue = formatValue ? formatValue(value) : value;
  const displayGoal = goal && formatValue ? formatValue(goal) : goal;
  const displayExpected = expected && formatValue ? formatValue(expected) : expected;

  return (
    <div className="metric-card" style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase', color: '#64748b' }}>{label}</h3>
      <div style={{ fontSize: '32px', fontWeight: 700, marginBottom: '12px' }}>{displayValue}</div>
      {goal && (
        <div style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6' }}>
          <div>Goal: {displayGoal}</div>
          {expected !== undefined && (
            <div style={{ marginTop: '4px', color: '#64748b' }}>Expected by Day {businessDay}: {displayExpected}</div>
          )}
        </div>
      )}
      {subtext && (
        <div style={{ fontSize: '14px', color: '#64748b' }}>{subtext}</div>
      )}
    </div>
  );
}

function SmallMetricCard({ label, value, color }) {
  return (
    <div style={{
      padding: '12px',
      backgroundColor: 'white',
      borderRadius: '6px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, color: color || '#1e293b' }}>{value}</div>
    </div>
  );
}

function ConversionCard({ label, rate, historicalAvg }) {
  const diff = rate - historicalAvg;
  const isUp = diff >= 0;

  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px' }}>{rate.toFixed(1)}%</div>
      <div style={{ fontSize: '12px', color: '#64748b' }}>
        <strong>6-Mo Avg:</strong> {historicalAvg.toFixed(1)}%
        <span style={{ marginLeft: '6px', color: isUp ? '#10b981' : '#ef4444', fontWeight: 600 }}>
          {isUp ? '↗' : '↘'} {isUp ? '+' : ''}{diff.toFixed(1)}pp
        </span>
      </div>
    </div>
  );
}

function GapRow({ metric, current, goal, remaining, isCurrency }) {
  const gap = goal - current;
  const dailyNeeded = remaining > 0 ? Math.ceil(gap / remaining) : 0;
  const formatValue = (v) => isCurrency ? `$${(v / 1000).toFixed(0)}k` : v;

  return (
    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
      <td style={{ padding: '8px', fontWeight: 500 }}>{metric}</td>
      <td style={{ padding: '8px', textAlign: 'right' }}>{formatValue(current)}</td>
      <td style={{ padding: '8px', textAlign: 'right' }}>{formatValue(goal)}</td>
      <td style={{ padding: '8px', textAlign: 'right', color: gap > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>
        {gap > 0 ? '-' : '+'}{formatValue(Math.abs(gap))}
      </td>
      <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>
        {gap <= 0 ? '✓ Done' : `${formatValue(dailyNeeded)}/day`}
      </td>
    </tr>
  );
}

function FunnelStage({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center', minWidth: '70px' }}>
      <div style={{
        backgroundColor: color,
        color: 'white',
        padding: '12px 16px',
        borderRadius: '8px',
        fontWeight: 700,
        fontSize: '20px',
        marginBottom: '8px'
      }}>
        {value}
      </div>
      <div style={{ fontSize: '11px', fontWeight: 600, color: '#475569', textTransform: 'uppercase' }}>{label}</div>
    </div>
  );
}

function FunnelArrow({ rate }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ fontSize: '24px', color: '#cbd5e1' }}>→</div>
      <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>{rate}</div>
    </div>
  );
}

function ChannelCard({ label, value, calculation }) {
  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '6px' }}>{value}</div>
      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{calculation}</div>
    </div>
  );
}

function EnhancementItem({ title, description }) {
  return (
    <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '6px' }}>
      <strong style={{ color: '#10b981' }}>{title}</strong>
      <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#64748b' }}>{description}</p>
    </div>
  );
}
